import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState({
    id: '',
    firstName: '', lastName: '', avatarUrl: '',
    rank: '', branch: '', yearsOfService: '',
    separationDate: '', clearance: '', careerGoal: '',
    careerGoalCustom: '', workType: '', location: '',
    bio: '', veteranVerified: false,
    profileViews: 0, recruiterViews: 0, jobMatches: 0,
    displayIdentity: 'veteran',
    lookingStatus: null,
    visibility: {
      military: 'private', clearance: 'private',
      certifications: 'private', skills: 'private',
      contact: 'private', resume: 'private',
    },
  });
  const [occupation,    setOccupation]    = useState(null);
  const [careerHistory, setCareerHistory] = useState([]);
  const [certs,         setCerts]         = useState([]);
  const [techSkills,    setTechSkills]    = useState([]);
  const [leadSkills,    setLeadSkills]    = useState([]);
  const [mentorFocus,   setMentorFocus]   = useState('');
  const [loading,       setLoading]       = useState(true);

  const fetchProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { setLoading(false); return; }

    const { data, error } = await supabase
      .from('profiles')
      .select(
        'first_name, last_name, avatar_url, rank, branch, years_of_service, ' +
        'separation_date, clearance, career_goal, career_goal_custom, work_type, ' +
        'location, bio, veteran_verified, profile_views, recruiter_views, ' +
        'job_matches, occupation_branch, occupation_code, occupation_title, ' +
        'career_history, certifications, tech_skills, lead_skills, mentor_focus, ' +
        'display_identity, looking_status, visibility'
      )
      .eq('id', session.user.id)
      .single();

    if (!error && data) {
      setProfile({
        id: session.user.id,
        firstName:        data.first_name        || '',
        lastName:         data.last_name         || '',
        avatarUrl:        data.avatar_url        || '',
        rank:             data.rank              || '',
        branch:           data.branch            || '',
        yearsOfService:   data.years_of_service  || '',
        separationDate:   data.separation_date   || '',
        clearance:        data.clearance         || '',
        careerGoal:       data.career_goal       || '',
        careerGoalCustom: data.career_goal_custom || '',
        workType:         data.work_type         || '',
        location:         data.location          || '',
        bio:              data.bio               || '',
        veteranVerified:  data.veteran_verified  || false,
        profileViews:     data.profile_views     || 0,
        recruiterViews:   data.recruiter_views   || 0,
        jobMatches:       data.job_matches       || 0,
        displayIdentity:  data.display_identity  || 'veteran',
        lookingStatus:    data.looking_status    || null,
        visibility:       data.visibility        || {
          military: 'private', clearance: 'private',
          certifications: 'private', skills: 'private',
          contact: 'private', resume: 'private',
        },
      });
      if (data.occupation_code) {
        setOccupation({
          branch: data.occupation_branch,
          code:   data.occupation_code,
          title:  data.occupation_title,
        });
      }
      if (data.career_history?.length > 0) setCareerHistory(data.career_history);
      if (data.certifications)  setCerts(data.certifications);
      if (data.tech_skills)     setTechSkills(data.tech_skills);
      if (data.lead_skills)     setLeadSkills(data.lead_skills);
      if (data.mentor_focus)    setMentorFocus(data.mentor_focus);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Initial fetch (covers case where session is already hydrated)
    fetchProfile();

    // Re-fetch on auth changes: handles the race where Supabase hydrates
    // the cached session AFTER initial mount, plus login/logout transitions.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        if (session?.user) fetchProfile();
      } else if (event === 'SIGNED_OUT') {
        setProfile({
          id: '',
          firstName: '', lastName: '', avatarUrl: '',
          rank: '', branch: '', yearsOfService: '',
          separationDate: '', clearance: '', careerGoal: '',
          careerGoalCustom: '', workType: '', location: '',
          bio: '', veteranVerified: false,
          profileViews: 0, recruiterViews: 0, jobMatches: 0,
          displayIdentity: 'veteran',
          lookingStatus: null,
          visibility: {
            military: 'private', clearance: 'private',
            certifications: 'private', skills: 'private',
            contact: 'private', resume: 'private',
          },
        });
        setOccupation(null);
        setCareerHistory([]);
        setCerts([]);
        setTechSkills([]);
        setLeadSkills([]);
        setMentorFocus('');
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  return (
    <ProfileContext.Provider value={{
      profile,      setProfile,
      occupation,   setOccupation,
      careerHistory, setCareerHistory,
      certs,        setCerts,
      techSkills,   setTechSkills,
      leadSkills,   setLeadSkills,
      mentorFocus,  setMentorFocus,
      loading,
      refreshProfile: fetchProfile,
    }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}